import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Zap, AlertTriangle, CheckCircle2, Loader2, TrendingUp, Wrench, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MOIS_ABBR = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const fmtEur = (v) => v == null ? '—' : (v.toFixed(2).replace('.', ',') + ' €');
const fmtKwh = (v) => v == null ? '—' : v.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const fmtTan = (v) => v == null ? '—' : v.toFixed(3).replace('.', ',');

const ReactifDiagnosticBlock = ({ pdl, tousLesPoints, pasTempsMinutes, domaineTension, hasInductiveData, hasCapacitiveData }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  // Si pas de données inductive/capacitive réelles, on masque le bloc
  if (!hasInductiveData && !hasCapacitiveData) return null;

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      // Préparer les samples (limités à 100k pour éviter la charge)
      const samples = tousLesPoints
        .filter(p => p.kVAr_inductif != null || p.kVAr_capacitif != null)
        .map(p => ({
          timestamp: new Date(p.horodate).toISOString(),
          p_w: (p.kW || 0) * 1000,
          pri_var: (p.kVAr_inductif || 0) * 1000,
          prc_var: (p.kVAr_capacitif || 0) * 1000,
        }));
      if (samples.length === 0) {
        toast.error('Aucune donnée réactive inductive/capacitive disponible');
        return;
      }
      const { data } = await api.post('/turpe/reactif/analyze', {
        samples,
        step_minutes: pasTempsMinutes || 5,
        domaine_tension: domaineTension || 'BT > 36 kVA',
      });
      setResult(data);
      toast.success('Analyse réactive terminée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse réactive');
    } finally {
      setAnalyzing(false);
    }
  };

  const profileBadge = (profil) => {
    const conf = {
      INDUCTIF_DOMINANT: { bg: 'bg-blue-100 text-blue-700', label: 'Profil inductif dominant' },
      CAPACITIF_DOMINANT: { bg: 'bg-yellow-100 text-yellow-700', label: 'Profil capacitif dominant' },
      MIXTE: { bg: 'bg-purple-100 text-purple-700', label: 'Profil mixte' },
      NEGLIGEABLE: { bg: 'bg-green-100 text-green-700', label: 'Réactif négligeable' },
    };
    const c = conf[profil] || conf.NEGLIGEABLE;
    return <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${c.bg}`}>{c.label}</span>;
  };

  const equipmentIcon = (type) => {
    if (type === 'BATTERIE_CONDENSATEURS') return <Wrench className="w-5 h-5 text-blue-600" />;
    if (type === 'INDUCTANCE_COMPENSATION') return <Wrench className="w-5 h-5 text-yellow-600" />;
    if (type === 'COMPENSATION_AUTOMATIQUE') return <Wrench className="w-5 h-5 text-purple-600" />;
    return <CheckCircle2 className="w-5 h-5 text-green-600" />;
  };

  // Graphique : énergie réactive par mois
  const chartData = result ? Object.entries(result.penalties.monthly).sort().map(([key, m]) => ({
    mois: MOIS_ABBR[parseInt(key.split('-')[1])] + ' ' + key.split('-')[0].slice(2),
    inductif: m.kvarh_inductif,
    capacitif: m.kvarh_capacitif,
  })) : [];

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-[#F9C80E] rounded-lg p-5 my-6" data-testid={`reactif-diagnostic-${pdl}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-6 h-6 text-[#F9C80E]" />
          <h3 className="text-lg font-semibold text-[#191D1E]">Diagnostic Énergie Réactive — PDL {pdl}</h3>
        </div>
        {!result && (
          <button onClick={runAnalysis} disabled={analyzing}
            className="flex items-center gap-2 bg-[#F9C80E] hover:bg-[#E5B800] text-[#191D1E] px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-50"
            data-testid={`analyze-reactif-${pdl}`}>
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {analyzing ? 'Analyse…' : 'Lancer l\'analyse'}
          </button>
        )}
      </div>

      {!result && !analyzing && (
        <div className="text-sm text-[#687175] bg-white rounded p-3">
          Les données ENEDIS détectées contiennent {hasInductiveData && hasCapacitiveData
            ? 'les colonnes PRI (inductive) et PRC (capacitive)'
            : hasInductiveData
              ? 'la colonne PRI (inductive, aucune donnée capacitive)'
              : 'la colonne PRC (capacitive, aucune donnée inductive)'}. Lancez l'analyse pour
          calculer les pénalités TURPE 7 et obtenir une recommandation d'équipement.
        </div>
      )}

      {result && (
        <>
          {/* Résumé annuel */}
          <div className={`grid gap-3 mb-5 ${hasInductiveData && hasCapacitiveData ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-white rounded p-3 border border-[#BFC6CC]">
              <div className="text-xs text-[#687175]">Énergie active</div>
              <div className="font-bold text-[#191D1E]">{fmtKwh(result.aggregate.total.kwh_active)} kWh</div>
            </div>
            {hasInductiveData && (
              <div className="bg-white rounded p-3 border border-blue-200">
                <div className="text-xs text-[#687175]">Réactif inductif</div>
                <div className="font-bold text-blue-700">{fmtKwh(result.aggregate.total.kvarh_inductif)} kVArh</div>
              </div>
            )}
            {hasCapacitiveData && (
              <div className="bg-white rounded p-3 border border-yellow-200">
                <div className="text-xs text-[#687175]">Réactif capacitif</div>
                <div className="font-bold text-yellow-700">{fmtKwh(result.aggregate.total.kvarh_capacitif)} kVArh</div>
              </div>
            )}
            <div className="bg-white rounded p-3 border border-[#BFC6CC]">
              <div className="text-xs text-[#687175]">tan φ moyen</div>
              <div className="font-bold text-[#191D1E]">{fmtTan(result.aggregate.total.tan_phi_moyen)}</div>
            </div>
          </div>

          {/* Badge profil */}
          <div className="mb-4">{profileBadge(result.recommendation.profil)}</div>

          {/* Graphique empilé mensuel */}
          <div className="bg-white rounded-lg p-3 border border-[#BFC6CC] mb-5">
            <div className="text-xs font-medium text-[#687175] uppercase mb-2">Énergie réactive par mois (kVArh)</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtKwh(v) + ' kVArh'} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="inductif" stackId="a" fill="#397DFF" name="Inductif" hide={!hasInductiveData} />
                <Bar dataKey="capacitif" stackId="a" fill="#F9C80E" name="Capacitif" hide={!hasCapacitiveData} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau mensuel */}
          <div className="bg-white rounded-lg border border-[#BFC6CC] overflow-hidden mb-5" data-testid={`reactif-monthly-table-${pdl}`}>
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6F9] text-xs uppercase text-[#687175]">
                <tr>
                  <th className="text-left px-3 py-2">Mois</th>
                  <th className="text-right px-3 py-2">kWh</th>
                  {hasInductiveData && <th className="text-right px-3 py-2">kVArh ind.</th>}
                  {hasCapacitiveData && <th className="text-right px-3 py-2">kVArh cap.</th>}
                  <th className="text-right px-3 py-2">tan φ</th>
                  {hasInductiveData && <th className="text-right px-3 py-2">Pén. Ind.</th>}
                  {hasCapacitiveData && <th className="text-right px-3 py-2">Pén. Cap.</th>}
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.penalties.monthly).sort().map(([key, m]) => (
                  <tr key={key} className="border-t border-[#F5F6F9]">
                    <td className="px-3 py-1.5 font-medium">{MOIS_ABBR[parseInt(key.split('-')[1])]} {key.split('-')[0].slice(2)}</td>
                    <td className="text-right font-mono">{fmtKwh(m.kwh_active)}</td>
                    {hasInductiveData && <td className="text-right font-mono text-blue-700">{fmtKwh(m.kvarh_inductif)}</td>}
                    {hasCapacitiveData && <td className="text-right font-mono text-yellow-700">{fmtKwh(m.kvarh_capacitif)}</td>}
                    <td className="text-right font-mono">{fmtTan(m.tan_phi)}</td>
                    {hasInductiveData && <td className="text-right font-mono font-semibold text-red-600">{m.penalty_inductif_eur > 0 ? fmtEur(m.penalty_inductif_eur) : '—'}</td>}
                    {hasCapacitiveData && <td className="text-right font-mono font-semibold text-red-600">{m.penalty_capacitif_eur > 0 ? fmtEur(m.penalty_capacitif_eur) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F5F6F9] font-semibold">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="text-right px-3 py-2 font-mono">{fmtKwh(result.aggregate.total.kwh_active)}</td>
                  {hasInductiveData && <td className="text-right px-3 py-2 font-mono text-blue-700">{fmtKwh(result.aggregate.total.kvarh_inductif)}</td>}
                  {hasCapacitiveData && <td className="text-right px-3 py-2 font-mono text-yellow-700">{fmtKwh(result.aggregate.total.kvarh_capacitif)}</td>}
                  <td className="text-right px-3 py-2 font-mono">{fmtTan(result.aggregate.total.tan_phi_moyen)}</td>
                  {hasInductiveData && <td className="text-right px-3 py-2 font-mono text-red-600" data-testid={`total-penalty-ind-${pdl}`}>{fmtEur(result.penalties.total.penalty_inductif_eur)}</td>}
                  {hasCapacitiveData && <td className="text-right px-3 py-2 font-mono text-red-600" data-testid={`total-penalty-cap-${pdl}`}>{fmtEur(result.penalties.total.penalty_capacitif_eur)}</td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Recommandation */}
          <div className={`flex items-start gap-3 p-4 rounded-lg border-2 ${result.recommendation.type_equipement === 'AUCUN' ? 'bg-green-50 border-green-300' : 'bg-white border-[#F9C80E]'}`} data-testid={`reco-${pdl}`}>
            {equipmentIcon(result.recommendation.type_equipement)}
            <div className="flex-1">
              <div className="font-semibold text-[#191D1E] mb-1">{result.recommendation.titre}</div>
              <div className="text-sm text-[#687175]">{result.recommendation.description}</div>
              {result.recommendation.gain_annuel_estime_eur > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-green-700">
                    Économie annuelle estimée : {fmtEur(result.recommendation.gain_annuel_estime_eur)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Paramètres utilisés */}
          <div className="mt-3 text-xs text-[#687175] flex items-center gap-4 flex-wrap">
            <span>Domaine : <strong>{result.domaine_tension}</strong></span>
            {hasInductiveData && <span>Seuil inductif : <strong>{result.config_used.seuil_inductif}</strong></span>}
            {hasCapacitiveData && <span>Seuil capacitif : <strong>{result.config_used.seuil_capacitif}</strong></span>}
            {hasInductiveData && <span>Prix ind. : <strong>{result.config_used.prix_inductif_eur_kvarh} €/kVArh</strong></span>}
            {hasCapacitiveData && <span>Prix cap. : <strong>{result.config_used.prix_capacitif_eur_kvarh} €/kVArh</strong></span>}
          </div>
        </>
      )}
    </div>
  );
};

export default ReactifDiagnosticBlock;
